import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { saveAs } from 'file-saver';
import {
  getAllSocieties,
  getAccountingPreview,
  downloadAccountingXlsx,
  downloadAccountingZip
} from '../services/api.service';
import Loading from '../components/loading';
import Helper from '../services/helper.service';
import dayjs from 'dayjs';

const fmt = (n) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2
  }).format(Number(n || 0));

export default function AccountingExportPage() {
  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);

  // Preset ranges
  const {
    startOfMonth, endOfMonth,
    lastMonthStart, lastMonthEnd,
    startOfYear, endOfYear,
    lastYearStart, lastYearEnd
  } = useMemo(() => {
    const now = dayjs();
    return {
      startOfMonth: now.startOf('month').format('YYYY-MM-DD'),
      endOfMonth: now.endOf('month').format('YYYY-MM-DD'),
      lastMonthStart: now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      lastMonthEnd: now.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      startOfYear: now.startOf('year').format('YYYY-MM-DD'),
      endOfYear: now.endOf('year').format('YYYY-MM-DD'),
      lastYearStart: now.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
      lastYearEnd: now.subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
    };
  }, []);

  const [start, setStart] = useState(startOfMonth);
  const [end, setEnd] = useState(endOfMonth);
  const [societyId, setSocietyId] = useState('');
  const [includeRefunds, setIncludeRefunds] = useState(true);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState({ count: 0, total: 0, currency: 'EUR' });

  // active preset helper
  const isPreset = useCallback((a, b) => start === a && end === b, [start, end]);

  // small styling helper for preset buttons
  const quickBtn = (active) =>
    `px-3 py-1.5 text-sm rounded-md border transition
     ${active ? 'bg-primary text-white border-primary'
      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`;

  // Charger les sociétés au montage
  useEffect(() => {
    const run = async () => {
      setLoadingSocieties(true);
      try {
        const data = await getAllSocieties();
        setSocieties(Array.isArray(data) ? data : []);
        const def = (data || []).find(s => s.useByDefault) || (data || [])[0];
        if (def?._id) setSocietyId(def._id);
      } catch (e) {
        Helper.toast("Erreur lors du chargement des sociétés", "error");
      } finally {
        setLoadingSocieties(false);
      }
    };
    run();
  }, []);

  // Prévisualisation quand filtres complets
  useEffect(() => {
    if (!societyId || !start || !end) return;
    let alive = true;

    (async () => {
      setPreviewLoading(true);
      try {
        const res = await getAccountingPreview({ societyId, start, end, includeRefunds });
        if (!alive) return;
        setPreview({
          count: res?.count || 0,
          total: res?.total || 0,
          currency: res?.currency || 'EUR'
        });
      } catch (e) {
        if (!alive) return;
        setPreview({ count: 0, total: 0, currency: 'EUR' });
        Helper.toast("Erreur lors de la prévisualisation", "error");
      } finally {
        if (alive) setPreviewLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [societyId, start, end, includeRefunds]);

  const canExport = societyId && start && end && preview?.count > 0 && !previewLoading;

  const onExportXlsx = async () => {
    try {
      const blob = await downloadAccountingXlsx({ societyId, start, end, includeRefunds });
      saveAs(blob, `export-comptable_${dayjs(start).format('YYYYMMDD')}-${dayjs(end).format('YYYYMMDD')}.xlsx`);
    } catch {
      Helper.toast("Échec du téléchargement Excel", "error");
    }
  };

  const onDownloadZip = async () => {
    try {
      const blob = await downloadAccountingZip({ societyId, start, end, includeRefunds });
      saveAs(blob, `factures_${dayjs(start).format('YYYYMMDD')}-${dayjs(end).format('YYYYMMDD')}.zip`);
    } catch {
      Helper.toast("Échec du téléchargement du ZIP", "error");
    }
  };

  return (
    <section className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Export comptable</h1>
        <p className="text-sm text-gray-600 mt-1">Choisissez une société et une période, puis exportez l’Excel et/ou les factures.</p>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        {loadingSocieties ? (
          <div className="py-10 flex justify-center"><Loading /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Presets (full width) */}
            <div className="md:col-span-4">
              <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setStart(startOfMonth); setEnd(endOfMonth); }}
                  className={quickBtn(isPreset(startOfMonth, endOfMonth))}
                  aria-pressed={isPreset(startOfMonth, endOfMonth)}
                >
                  Ce mois
                </button>
                <button
                  type="button"
                  onClick={() => { setStart(lastMonthStart); setEnd(lastMonthEnd); }}
                  className={quickBtn(isPreset(lastMonthStart, lastMonthEnd))}
                  aria-pressed={isPreset(lastMonthStart, lastMonthEnd)}
                >
                  Mois dernier
                </button>
                <button
                  type="button"
                  onClick={() => { setStart(startOfYear); setEnd(endOfYear); }}
                  className={quickBtn(isPreset(startOfYear, endOfYear))}
                  aria-pressed={isPreset(startOfYear, endOfYear)}
                >
                  Cette année
                </button>
                <button
                  type="button"
                  onClick={() => { setStart(lastYearStart); setEnd(lastYearEnd); }}
                  className={quickBtn(isPreset(lastYearStart, lastYearEnd))}
                  aria-pressed={isPreset(lastYearStart, lastYearEnd)}
                >
                  Année dernière
                </button>
              </div>
            </div>

            {/* Société */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Société</label>
              <select
                value={societyId}
                onChange={e => setSocietyId(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="" disabled>-- Sélectionner --</option>
                {societies.map(s => (
                  <option key={s._id} value={s._id}>
                    {s.name} {s.useByDefault ? '— (par défaut)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Début */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Début</label>
              <input
                type="date"
                value={start}
                max={end || undefined}
                onChange={e => setStart(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            {/* Fin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fin</label>
              <input
                type="date"
                value={end}
                min={start || undefined}
                onChange={e => setEnd(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            {/* Inclure remboursements */}
            <div className="md:col-span-4">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  className="accent-primary"
                  checked={includeRefunds}
                  onChange={e => setIncludeRefunds(e.target.checked)}
                />
                <span className="text-sm text-gray-800">Inclure les transactions remboursées/annulées</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Prévisualisation */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        {previewLoading ? (
          <div className="py-10 flex justify-center"><Loading /></div>
        ) : !societyId || !start || !end ? (
          <p className="text-sm text-gray-600">Sélectionnez une société et une période pour voir le récapitulatif.</p>
        ) : (
          <>
            <p className="text-sm text-gray-700">
              Pour la période <strong>{dayjs(start).format('DD/MM/YYYY')}</strong> au <strong>{dayjs(end).format('DD/MM/YYYY')}</strong> :
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500 uppercase">Transactions</div>
                <div className="text-xl font-semibold">{preview.count}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500 uppercase">Montant total</div>
                <div className="text-xl font-semibold">{fmt(preview.total)}</div>
              </div>
            </div>
            {preview.count === 0 && (
              <p className="mt-3 text-xs text-gray-500">Aucune transaction trouvée pour ces critères.</p>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col md:flex-row gap-3">
        <button
          onClick={onExportXlsx}
          disabled={!canExport}
          className={`px-4 py-2 rounded text-white ${canExport ? 'bg-primary hover:bg-primary/90' : 'bg-gray-400 cursor-not-allowed'}`}
        >
          Export Excel
        </button>
        <button
          onClick={onDownloadZip}
          disabled={!canExport}
          className={`px-4 py-2 rounded text-white ${canExport ? 'bg-gray-800 hover:bg-black' : 'bg-gray-400 cursor-not-allowed'}`}
        >
          Télécharger les factures (ZIP)
        </button>
      </div>
    </section>
  );
}